import { useCallback, useEffect, useState } from 'react'

import { getFirebase } from '~/firebase'

import {
  PetState,
  resetPetStateActionCreator,
  selectPetState,
  setPetStateActionCreator,
} from '~/redux/slices/pet-state'
import { selectUserId } from '~/auth/slices/auth.slice'
import { useAppSelector, useAppDispatch } from '~/redux/hooks'

export default function usePetState(): [PetState, boolean] {
  const reduxPetState: PetState = useAppSelector(selectPetState) as PetState
  const [loading, setLoading] = useState<boolean>(true)
  const dispatch = useAppDispatch()
  const id = useAppSelector(selectUserId)

  const fetchPetState = useCallback(
    (userId: number) => {
      const run = async (): Promise<() => void> => {
        const { doc, fireStore, onSnapshot, getDoc } = await getFirebase()
        const petStateDocRef = doc(fireStore, 'users', String(userId), 'homebase', 'pet-state')
        const pointsDocRef = doc(fireStore, 'users', String(userId), 'awards', 'points')

        return onSnapshot(petStateDocRef, async (_doc) => {
          if (_doc.exists()) {
            const petStateData = _doc.data() as PetState
            const pointsData = await getDoc(pointsDocRef)
            if (pointsData.exists()) {
              petStateData.kudosOrbs = pointsData.data()?.mathEnergyOrbs || 0
            }
            if (!petStateData) {
              dispatch(resetPetStateActionCreator())
            } else {
              dispatch(setPetStateActionCreator(petStateData))
            }
          }
          setLoading(false)
        })
      }

      return run()
    },
    [dispatch],
  )

  useEffect(() => {
    let unsubscribe: (() => void) | undefined
    let isMounted = true

    if (!id) return () => {}
    ;(async () => {
      const unsub = await fetchPetState(id)
      if (isMounted) {
        unsubscribe = unsub
      } else if (unsub) {
        unsub()
      }
    })()

    return () => {
      isMounted = false
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [id, fetchPetState])

  return [reduxPetState, loading] as const
}
